interface ReadReceiptMessage {
  id: string
  author: { id: string }
  /** ISO timestamp of when the message was created. */
  createdAt: string
}

/** The API's times are UTC; one sent without a zone ('2026-06-25 15:12:08.268') must not be read as local time. */
const toUtcMs = (value: string): number => {
  const iso = value.trim().replace(' ', 'T')
  return new Date(/(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`).getTime()
}

/**
 * Read receipt: the id of the most recent message *I* sent that the other
 * person has already read (their last-read time is at or after it). Only that
 * one message shows "Vu", WhatsApp-style.
 *
 * Returns `undefined` when there is no data, no current user, no readable read
 * time, or none of my sent messages were read yet.
 */
export function computeLastReadSentMessageId<T extends ReadReceiptMessage>(
  messages: T[] | null | undefined,
  currentUserAccountId: string | null | undefined,
  othersLastReadAt: string | null | undefined,
): string | undefined {
  if (!messages || !currentUserAccountId || !othersLastReadAt) return undefined

  const readAtMs = toUtcMs(othersLastReadAt)
  // An unreadable time shows no "Vu" rather than a wrong one.
  if (Number.isNaN(readAtMs)) return undefined

  const readSent = messages
    .filter(
      (m) => m.author.id === currentUserAccountId && new Date(m.createdAt).getTime() <= readAtMs,
    )
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  return readSent[0]?.id
}

/**
 * The id of the most recent message someone else sent, in a list sorted
 * newest first (as the API returns it). When it changes while the
 * conversation is on screen, a message has just been received.
 *
 * Returns `undefined` when there is no data, no current user, or only my own
 * messages.
 */
export function latestReceivedMessageId<T extends Pick<ReadReceiptMessage, 'id' | 'author'>>(
  messages: T[] | null | undefined,
  currentUserAccountId: string | null | undefined,
): string | undefined {
  if (!messages || !currentUserAccountId) return undefined
  return messages.find((m) => m.author.id !== currentUserAccountId)?.id
}
